import React, { useMemo } from "react";
import { HeuristicBlock } from "./paeiPrimitives";

// All nine demand shifts, tagged applied / not applied against this run.
//
// This tab exists so Empower's relocation case stays documented even on the runs
// that never fire it — the demand it moves is the one thing about this model that
// is easy to miss and expensive to miss.
function PaeiShiftReferenceTab({ reference, referenceError, result }) {
  const appliedNames = useMemo(() => {
    const names = new Set();
    (result?.redesign?.applied || []).forEach((n) => names.add(String(n).toLowerCase()));
    (result?.redesign?.heuristics || []).forEach((h) =>
      names.add(String(h.heuristic || "").toLowerCase()),
    );
    return names;
  }, [result]);

  // Prefer the run's own copy of an applied heuristic — it carries the task
  // targets and the deltas actually used — and fall back to the catalogue entry.
  const appliedByName = useMemo(() => {
    const map = new Map();
    (result?.redesign?.heuristics || []).forEach((h) =>
      map.set(String(h.heuristic || "").toLowerCase(), h),
    );
    return map;
  }, [result]);

  if (referenceError) {
    return (
      <div className="pa-panel">
        <div className="pa-banner warn">
          The shift reference could not be loaded: {referenceError}
        </div>
      </div>
    );
  }

  if (!reference || reference.length === 0) {
    return (
      <div className="pa-panel">
        <div className="pa-empty">Loading the demand-shift reference…</div>
      </div>
    );
  }

  const appliedCount = reference.filter((h) =>
    appliedNames.has(String(h.heuristic).toLowerCase()),
  ).length;

  return (
    <div className="pa-panel">
      <p className="pa-card-hint">
        Every demand shift the agent can apply, whether or not this run used one. A shift never
        removes work for free — it trades a control, a hand-off or a re-verification for the
        capability the step stops needing.
      </p>

      <div className="pa-summary-strip">
        <span>
          <b>{appliedCount}</b> of <b>{reference.length}</b> applied in this run
        </span>
      </div>

      <div className="pa-heuristic-list">
        {reference.map((h) => {
          const key = String(h.heuristic).toLowerCase();
          const applied = appliedNames.has(key);
          return (
            <HeuristicBlock
              key={h.heuristic}
              item={applied ? { ...h, ...appliedByName.get(key) } : h}
              applied={applied}
            />
          );
        })}
      </div>
    </div>
  );
}

export default PaeiShiftReferenceTab;
